import { requiredConfigSchema, runtimeConfigSchema, type FeatureFlagName, type RuntimeConfig } from './schema';

/** Mesmo bucket público do legado (`services/config/index.ts`), um JSON por hostname. */
export const CONFIG_BUCKET_URL = 'https://uyzy-config.s3.sa-east-1.amazonaws.com';
export const LOCAL_CONFIG_PATH = '/config.local.json';
/** JSON publicado junto com o site pelo pipeline (PLANO-ESTEIRA-STG §D3): deploy fora do bucket do legado. */
export const DEPLOY_CONFIG_PATH = '/config.json';
/** Mesma chave de override do legado: `'local'` ou o nome de um domínio do bucket. */
export const CONFIG_OVERRIDE_KEY = 'localConfig';

export class RuntimeConfigError extends Error {
  override readonly name = 'RuntimeConfigError';
  readonly source: string;
  readonly issues: readonly string[];
  constructor(message: string, source: string, issues: readonly string[] = []) {
    super(message);
    this.source = source;
    this.issues = issues;
  }
}

interface ResolveInput {
  hostname: string;
  override: string | null;
  isDev: boolean;
}

/** De onde vem o JSON: override → local; override de domínio → bucket; dev → local; senão bucket/{hostname}. */
export function resolveConfigSource({ hostname, override, isDev }: ResolveInput): string {
  if (override === 'local') return LOCAL_CONFIG_PATH;
  if (override) return `${CONFIG_BUCKET_URL}/${override}.json`;
  if (isDev) return LOCAL_CONFIG_PATH;
  return `${CONFIG_BUCKET_URL}/${hostname}.json`;
}

type Warn = (message: string, issues: readonly string[]) => void;

const describeIssues = (issues: readonly { path: readonly PropertyKey[]; message: string }[]) =>
  issues.map((issue) => `${issue.path.map(String).join('.') || '(raiz)'}: ${issue.message}`);

/**
 * Valida o JSON. Falta de chave obrigatória derruba o boot; tipo inesperado nas demais só avisa e
 * segue com o valor cru (U27: passthrough), porque cada domínio tem o seu JSON e o legado nunca validou.
 */
export function parseRuntimeConfig(raw: unknown, source: string, warn: Warn = defaultWarn): RuntimeConfig {
  const required = requiredConfigSchema.safeParse(raw);
  if (!required.success) {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- mensagem técnica; a tela mostra BootErrorScreen
    throw new RuntimeConfigError('Config de runtime sem as chaves obrigatórias', source, describeIssues(required.error.issues));
  }
  const full = runtimeConfigSchema.safeParse(raw);
  if (full.success) return full.data;
  // eslint-disable-next-line lingui/no-unlocalized-strings -- aviso de console em dev
  warn(`Config de runtime (${source}) com tipos inesperados; seguindo com os valores crus`, describeIssues(full.error.issues));
  return raw as RuntimeConfig;
}

function defaultWarn(message: string, issues: readonly string[]) {
  if (import.meta.env.PROD) return;
  console.warn(message, issues);
}

let current: RuntimeConfig | undefined;

interface LoadOptions {
  fetch?: typeof fetch;
  hostname?: string;
  override?: string | null;
  isDev?: boolean;
  warn?: Warn;
}

function readOverride(): string | null {
  try {
    return globalThis.localStorage.getItem(CONFIG_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

/** Baixa, valida e guarda a config. Chame uma vez no boot, antes do primeiro render. */
export async function loadRuntimeConfig(options: LoadOptions = {}): Promise<RuntimeConfig> {
  const override = options.override === undefined ? readOverride() : options.override;
  const isDev = options.isDev ?? import.meta.env.DEV;
  const source = resolveConfigSource({ hostname: options.hostname ?? globalThis.location.hostname, override, isDev });
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  if (!override && !isDev) {
    const deployed = await fetchDeployConfig(doFetch);
    if (deployed !== undefined) return keep(parseRuntimeConfig(deployed, DEPLOY_CONFIG_PATH, options.warn));
  }
  // Bootstrap fora de shared/http (U27): o legado também usava axios cru aqui, antes de existir API.
  const response = await doFetch(source, { cache: 'no-cache', credentials: 'omit' });
  if (!response.ok) {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- mensagem técnica; a tela mostra BootErrorScreen
    throw new RuntimeConfigError(`Config de runtime indisponível (HTTP ${String(response.status)})`, source);
  }
  const raw: unknown = await response.json();
  return keep(parseRuntimeConfig(raw, source, options.warn));
}

function keep(config: RuntimeConfig): RuntimeConfig {
  current = Object.freeze(config);
  return current;
}

/**
 * O `/config.json` do próprio site, se existir. Ausente (404) ou HTML (fallback de SPA do CloudFront do legado)
 * devolve `undefined`, e o boot segue para o bucket por hostname, como antes.
 */
async function fetchDeployConfig(doFetch: typeof fetch): Promise<unknown> {
  try {
    const response = await doFetch(DEPLOY_CONFIG_PATH, { cache: 'no-cache', credentials: 'omit' });
    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) return undefined;
    return (await response.json()) as unknown;
  } catch {
    return undefined;
  }
}

/** A config já carregada. Lança se o boot ainda não rodou `loadRuntimeConfig`. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!current) throw new Error('Config de runtime ainda não carregada: chame loadRuntimeConfig() no boot');
  return current;
}

/** Só para testes. */
export function setRuntimeConfigForTests(config: RuntimeConfig | undefined) {
  current = config;
}

/** Valor cru da flag, como `getFeatureFlagByName` do legado: ausente vira `null`. */
export function getFeatureFlag(config: RuntimeConfig, name: FeatureFlagName) {
  return config.FEATURE_FLAGS?.[name] ?? null;
}

/** Instância raiz (UY3): `TENANT_TYPE === 0`. */
export function isRootTenancy(config: RuntimeConfig) {
  return config.TENANT_TYPE === 0;
}

/** O seletor "Correspondente" aparece na raiz ou com `USE_TENANT_FILTER === 1` (discovery/03 §1.5). */
export function showsTenantFilter(config: RuntimeConfig) {
  return isRootTenancy(config) || config.USE_TENANT_FILTER === 1;
}
